import { Link } from "@tanstack/react-router";
import { FileText, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Generic post-lead-form deliverable CTA. Every product cluster promises
 * a specific PDF report the borrower receives after submitting the
 * short intake — this banner tells them what they get, and pushes them
 * to /apply with the right product preselected.
 *
 * Two visual variants: `full` (hero-size) and `inline` (compact).
 */

export type ReportCtaConfig = {
  /** value passed to /apply?product=... */
  productKey: string;
  /** short label shown in the pill/eyebrow */
  eyebrow: string;
  /** headline (locationLabel will be appended: " for your <label> property/deal") */
  headline: string;
  /** supporting paragraph */
  body: string;
  /** 3-5 short bullets describing what's inside the PDF */
  bullets: string[];
  /** CTA button label */
  cta: string;
  /** Mini "PDF preview" — 4-6 pairs of [label, illustrative value] */
  preview: Array<[string, string]>;
  /** small caption under bullets */
  caption?: string;
};

export const REPORT_CTAS: Record<string, ReportCtaConfig> = {
  dscr: {
    productKey: "dscr",
    eyebrow: "Free with your application",
    headline: "Get your DSCR Scenario Summary",
    body: "Complete the short pre-qualification form and we send back a full DSCR analysis: HUD Fair Market Rent, AVM value, calculated DSCR, PITIA breakdown, and the max qualifying loan amount up to 80% LTV.",
    bullets: [
      "HUD FMR rent estimate for the subject property",
      "AVM property value + max qualifying loan",
      "Calculated DSCR with PITIA breakdown",
      "Cash-on-cash and year-one cash flow",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["Est. rent (FMR)", "$2,840"],
      ["Property value", "$412,000"],
      ["Calculated DSCR", "1.28"],
      ["Max loan (80% LTV)", "$329,600"],
      ["PITIA", "$2,210"],
      ["Cash flow (Y1)", "$7,560"],
    ],
  },
  flip: {
    productKey: "flip",
    eyebrow: "Free with your application",
    headline: "Get your Fix & Flip Deal Analysis",
    body: "Send us the property and rehab budget — we send back a full flip underwriting: ARV, max loan to purchase, 100% rehab draw schedule, projected profit, and cash-on-cash return.",
    bullets: [
      "ARV & max loan to purchase (up to 90% LTP)",
      "100% rehab budget with draw schedule",
      "Projected net profit & margin",
      "Cash-on-cash and annualized return",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["Purchase price", "$225,000"],
      ["Rehab budget", "$48,000"],
      ["ARV", "$385,000"],
      ["Loan (90% LTP)", "$202,500"],
      ["Projected profit", "$62,400"],
      ["Cash-on-cash", "38.6%"],
    ],
  },
  construction: {
    productKey: "construction",
    eyebrow: "Free with your application",
    headline: "Get your Construction Budget & Scenario",
    body: "Send us the lot and budget — we return a full ground-up analysis: max loan-to-cost, interest reserve, draw schedule, projected stabilized value, and take-out DSCR.",
    bullets: [
      "Loan-to-cost & loan-to-ARV sizing",
      "Draw schedule with interest reserve",
      "Stabilized value & take-out DSCR",
      "Total project cost and required equity",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["Lot cost", "$95,000"],
      ["Hard costs", "$310,000"],
      ["Total cost", "$438,000"],
      ["Loan (85% LTC)", "$372,300"],
      ["Stabilized value", "$580,000"],
      ["Take-out DSCR", "1.22"],
    ],
  },
  multifamily: {
    productKey: "multifamily",
    eyebrow: "Free with your application",
    headline: "Get your Multifamily Underwriting Summary",
    body: "Send the unit count and rent roll — we return a commercial sizing summary: underwritten NOI, value at market cap rate, max loan at 75% LTV, DSCR, and the debt service the building actually supports.",
    bullets: [
      "Underwritten NOI from your rent roll and expenses",
      "Value at the market cap rate for your submarket",
      "Max loan at 75% LTV and at a 1.20x DSCR",
      "Monthly payment on a 30-year amortization",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["Units", "12"],
      ["Effective gross income", "$182,736"],
      ["Underwritten NOI", "$105,987"],
      ["Value at 6.5% cap", "$1,630,570"],
      ["Loan at 1.20x DSCR", "$1,065,000"],
      ["Payment (30-yr am)", "$7,356 / mo"],
    ],
  },
  hardmoney: {
    productKey: "hardmoney",
    eyebrow: "Free with your application",
    headline: "Get your Private Money Term Snapshot",
    body: "Send the property, the equity, and your exit — we return an asset-based sizing snapshot: max loan against as-is value, interest-only carry, points and fees, total hold cost, and the takeout that retires the loan.",
    bullets: [
      "Max loan against as-is (and after-repair) value",
      "Interest-only monthly carry and total hold cost",
      "Points, fees, and minimum-interest exposure",
      "Exit check — sale proceeds or DSCR refinance coverage",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["As-is value (BPO)", "$355,000"],
      ["Loan (75% as-is)", "$266,250"],
      ["Rate (I/O)", "11.25%"],
      ["Monthly carry", "$2,496"],
      ["Points", "2.0"],
      ["Exit", "DSCR refi @ 1.24x"],
    ],
  },
  bridge: {
    productKey: "bridge",
    eyebrow: "Free with your application",
    headline: "Get your Bridge Loan Scenario Summary",
    body: "Send us the property, purpose, and exit — we send back a bridge sizing summary: max loan, interest-only payment, points and fees, timeline, and exit strategy analysis.",
    bullets: [
      "Max loan at 75–80% LTV",
      "Interest-only monthly payment",
      "All-in cost estimate (points + fees)",
      "Exit strategy sanity check",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["Property value", "$540,000"],
      ["Loan (75% LTV)", "$405,000"],
      ["Rate (I/O)", "10.25%"],
      ["Monthly payment", "$3,459"],
      ["Term", "12 mo"],
      ["All-in cost", "2.5 pts + $2,995"],
    ],
  },
  portfolio: {
    productKey: "portfolio",
    eyebrow: "Free with your application",
    headline: "Get your Portfolio Rental Scenario Summary",
    body: "Send us your rent roll — we return a blanket portfolio underwriting: aggregate DSCR, blended LTV, cash-out proceeds, and a per-property qualification breakdown.",
    bullets: [
      "Aggregate portfolio DSCR",
      "Blended LTV and max loan",
      "Cash-out proceeds estimate",
      "Per-property qualification breakdown",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["Properties", "7"],
      ["Gross rent (mo)", "$18,420"],
      ["Aggregate DSCR", "1.34"],
      ["Blended LTV", "72%"],
      ["Portfolio loan", "$1,845,000"],
      ["Cash-out", "$412,000"],
    ],
  },
  foreign: {
    productKey: "foreign",
    eyebrow: "Free with your application",
    headline: "Get your Foreign National Eligibility Summary",
    body: "Send us the property and your country of residence — we return a full foreign national eligibility PDF: LTV tier, documentation checklist, rate range, and estimated closing costs.",
    bullets: [
      "LTV eligibility (up to 75%, 80% select cases)",
      "Country-specific documentation checklist",
      "Rate range and closing cost estimate",
      "LLC vesting & reserve requirements",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["Property value", "$625,000"],
      ["Loan (70% LTV)", "$437,500"],
      ["Estimated rate", "8.75%"],
      ["Reserves required", "12 mo PITIA"],
      ["Vesting", "US LLC"],
      ["Est. closing costs", "$18,400"],
    ],
  },
  "section-8": {
    productKey: "section-8",
    eyebrow: "Free with your application",
    headline: "Get your Section 8 Rent & Scenario Snapshot",
    body: "Send us the property — we return a full Section 8 underwriting: HUD FMR & Payment Standard, gross-to-market rent comparison, DSCR, and max qualifying loan.",
    bullets: [
      "HUD FMR & Payment Standard for the ZIP",
      "Section 8 vs. market rent comparison",
      "DSCR calculated on voucher rent",
      "Max qualifying loan at 75–80% LTV",
    ],
    cta: "Get Your Personalized Rate Quote",
    caption: "Takes ~3 minutes • PDF delivered on the thank-you page and emailed",
    preview: [
      ["HUD FMR (2BR)", "$1,685"],
      ["Payment Standard", "$1,854"],
      ["Market rent", "$1,750"],
      ["Calculated DSCR", "1.19"],
      ["Property value", "$185,000"],
      ["Max loan (80%)", "$148,000"],
    ],
  },
};

export function ProductReportCtaBanner({
  productKey,
  variant = "full",
  locationLabel,
}: {
  productKey: keyof typeof REPORT_CTAS | string;
  variant?: "full" | "inline" | "article";
  locationLabel?: string;
}) {
  const cfg = REPORT_CTAS[productKey];
  if (!cfg) return null;

  const where = locationLabel ? ` for your ${locationLabel} property` : "";

  if (variant === "article") {
    return (
      <aside
        data-cta-banner=""
        aria-label={cfg.headline}
        className="my-12 min-w-0 overflow-hidden rounded-lg border border-brand-gold/60 bg-brand-gradient text-brand-foreground shadow-xl"
      >
        <div className="grid min-w-0 gap-6 p-5 sm:p-7 md:grid-cols-[minmax(0,1fr)_minmax(13rem,0.48fr)] md:items-center">
          <div className="min-w-0">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-brand-gold/60 bg-brand-gold/15 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-brand-gold">
              <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{cfg.eyebrow}</span>
            </div>
            <h2 className="mt-4 break-words font-display text-2xl font-bold leading-tight text-brand-foreground sm:text-3xl">
              {cfg.headline}
              {where}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-brand-foreground/85 sm:text-base">
              {cfg.body}
            </p>
            <ul className="mt-5 grid gap-2.5 text-sm leading-snug sm:grid-cols-2">
              {cfg.bullets.slice(0, 4).map((line) => (
                <li key={line} className="flex min-w-0 items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" aria-hidden />
                  <span className="min-w-0 break-words text-brand-foreground/90">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="min-w-0 border-t border-brand-foreground/15 pt-5 md:border-l md:border-t-0 md:pl-6 md:pt-0">
            <div className="mb-4 flex items-center gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-brand-gold/20">
                <FileText className="h-5 w-5 text-brand-gold" aria-hidden />
              </div>
              <p className="min-w-0 text-sm font-semibold leading-snug text-brand-foreground">
                Your personalized report is included.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="h-auto min-h-12 w-full whitespace-normal bg-brand-gold px-4 py-3 text-center text-sm font-bold leading-snug text-brand hover:bg-brand-gold/90"
            >
              <Link to="/apply" search={{ product: cfg.productKey } as never}>
                {cfg.cta}
              </Link>
            </Button>
            {cfg.caption && (
              <p className="mt-3 break-words text-xs leading-relaxed text-brand-foreground/70">
                {cfg.caption}
              </p>
            )}
          </div>
        </div>
        <p className="border-t border-brand-foreground/15 px-5 py-3 text-[10px] leading-relaxed text-brand-foreground/60 sm:px-7">
          Illustration only, generated from the information you enter. Not a loan estimate, pre-qualification, commitment to lend, or approval. Formal pricing, LTV, and terms are issued only after credit review, income/asset documentation, and full underwriting.
        </p>
      </aside>
    );
  }

  if (variant === "inline") {
    return (
      <div data-cta-banner="" className="mt-8 mb-20 overflow-hidden rounded-3xl border-2 border-brand-gold/60 bg-gradient-to-br from-brand/95 via-brand to-brand/90 p-6 text-white shadow-2xl sm:p-9">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="hidden rounded-xl bg-brand-gold/20 p-3 sm:block">
              <FileText className="h-7 w-7 text-brand-gold" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-brand-gold/15 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-brand-gold">
                <Sparkles className="h-3.5 w-3.5" />
                {cfg.eyebrow}
              </div>
              <h3 className="mt-3 text-2xl font-black leading-tight sm:text-3xl">
                {cfg.headline}
                {where}
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-white/80 sm:text-base">{cfg.body}</p>
              <ul className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                {cfg.bullets.slice(0, 4).map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span className="text-white/90">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-2 lg:items-end">
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="h-auto min-h-14 w-full whitespace-normal px-5 py-3 text-center text-sm font-bold bg-brand-gold text-brand hover:bg-brand-gold/90 sm:w-auto sm:px-8 sm:text-base"
            >
              <Link to="/apply" search={{ product: cfg.productKey } as never}>
                {cfg.cta}
              </Link>
            </Button>
            {cfg.caption && <span className="text-xs text-white/70">{cfg.caption}</span>}
          </div>
        </div>
        <p className="mt-5 text-[10px] leading-snug text-white/60">
          Illustration only, generated from the information you enter. Not a loan estimate, pre-qualification, commitment to lend, or approval. Formal pricing, LTV, and terms are issued only after credit review, income/asset documentation, and full underwriting.
        </p>
      </div>
    );
  }

  return (
    <div data-cta-banner="" className="mt-8 mb-20 overflow-hidden rounded-3xl border border-brand-gold/50 bg-gradient-to-br from-brand/95 via-brand to-brand/90 p-6 text-white sm:p-10">
      <div className="grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-brand-gold/15 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-brand-gold">
            <Sparkles className="h-3.5 w-3.5" />
            {cfg.eyebrow}
          </div>
          <h3 className="mt-3 text-2xl font-black leading-tight sm:text-3xl">
            {cfg.headline}
            {where}.
          </h3>
          <p className="mt-3 max-w-xl text-sm text-white/80 sm:text-base">{cfg.body}</p>
          <ul className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            {cfg.bullets.map((line) => (
              <li key={line} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                <span className="text-white/90">{line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="bg-brand-gold text-brand hover:bg-brand-gold/90"
            >
              <Link to="/apply" search={{ product: cfg.productKey } as never}>
                {cfg.cta}
              </Link>
            </Button>
            {cfg.caption && <span className="text-xs text-white/70">{cfg.caption}</span>}
          </div>
          <p className="mt-4 max-w-xl text-[10px] leading-snug text-white/60">
            Illustration only, generated from the information you enter. Not a loan estimate, pre-qualification, commitment to lend, or approval. Formal pricing, LTV, and terms are issued only after credit review, income/asset documentation, and full underwriting.
          </p>
        </div>
        <div className="hidden md:flex md:justify-center">
          <div className="relative w-full max-w-[280px] -rotate-3 rounded-lg border border-white/20 bg-white p-4 text-brand shadow-2xl">
            <div className="h-6 rounded-sm bg-brand" />
            <div className="mt-3 text-[10px] font-black uppercase tracking-widest">
              {cfg.headline.replace(/^Get your /, "")}
            </div>
            <div className="mt-2 space-y-1.5">
              {cfg.preview.map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-center justify-between border-b border-brand/10 pb-1 text-[10px]"
                >
                  <span className="text-brand/70">{k}</span>
                  <span className="font-bold">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[8px] text-brand/60">
              Simply Approved Mortgages • NMLS #2620881
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
